import type { AppState } from '../state/default-state';
import { createDefaultState } from '../state/default-state';
import type { BodyState } from '../core/types';
import { BODY_IDS } from '../core/reference-data';
import { encode, type ShareableConfig } from '../share';
import { FIT_VIEW_CAMERA } from '../state/camera';

/**
 * The composition-root bridge between `state` and `share` (AD-2 layer table: `app`
 * may import both; `state` and `share` must NOT import each other). It projects the
 * live `AppState` to the shareable include-set, hydrates a full `AppState` directly
 * from a decoded config (no re-swap, AD-4), and owns the ONLY reads/writes of the
 * URL fragment. All `location`/`window` access is guarded so the module imports
 * cleanly in a headless unit test.
 */

/**
 * Project the live state to the exact shareable include-set (AD-4). Reads ONLY:
 * `centralBody`, per-body live `{ a, e }` (dropping `status`), `legendOrder`,
 * `sectorOverlayVisible`, `sectorCount`, `speedDaysPerSecond`, `scaleMode`. It MUST
 * NOT read `selectedBody`, `paused`, `time`, `phaseOffsets`, `camera`,
 * `requestedProfile`, `reducedEffects`, `activeRenderMode`, or `unavailableFeatures`
 * (the excluded set), so no session/device field can leak into a shared link.
 */
export function projectShareable(state: AppState): ShareableConfig {
  const bodies = {} as ShareableConfig['bodies'];
  for (const id of BODY_IDS) {
    const body = state.bodies.find((b) => b.id === id);
    // Fall back to the seed only if a body were somehow missing; live a/e otherwise.
    bodies[id] = body ? { a: body.a, e: body.e } : { a: 0, e: 0 };
  }
  return {
    centralBody: state.centralBody,
    bodies,
    legendOrder: [...state.legendOrder],
    sectorOverlayVisible: state.sectorOverlayVisible,
    sectorCount: state.sectorCount,
    speedDaysPerSecond: state.speedDaysPerSecond,
    scaleMode: state.scaleMode
  };
}

/**
 * The per-field fallback baseline passed into `decode`: the shareable projection of
 * the canonical default system. Every decode falls unsafe/missing fields back to
 * this, so a partially-corrupt fragment still yields a coherent default-anchored
 * config.
 */
export function defaultShareableConfig(): ShareableConfig {
  return projectShareable(createDefaultState());
}

/**
 * Reconstruct a full, valid `AppState` DIRECTLY from a decoded shareable config
 * (AD-4: hydrate, never re-swap). Start from a fresh default (which fixes
 * `selectedBody: 'earth'`, `paused: true`, `time: 0`, `phaseOffsets` all 0, all
 * bodies `active`, and the graphics defaults), then OVERWRITE the include-set AND
 * the camera: the default now seeds the zoomed ~Jupiter boot framing
 * (`DEFAULT_VIEW_CAMERA`), which would crop a restored config whose bodies sit
 * further out, so hydrate explicitly overrides it to `FIT_VIEW_CAMERA` (fit-all)
 * so every restored orbit is fully framed on load (Story 3.5). No command is ever
 * dispatched and `SwitchCentre` is never called: the encoded `a`/`e` are already
 * current-frame values under the stored centre, so setting `centralBody` and the
 * bodies directly reproduces an alternate-centre state exactly (periods recompute
 * automatically from the central body's mu in the selectors). A double-swap is
 * structurally impossible.
 */
export function hydrateFromConfig(config: ShareableConfig): AppState {
  const base = createDefaultState();
  const bodies: BodyState[] = BODY_IDS.map((id) => ({
    id,
    status: 'active',
    a: config.bodies[id].a,
    e: config.bodies[id].e
  }));
  return {
    ...base,
    camera: FIT_VIEW_CAMERA,
    centralBody: config.centralBody,
    bodies,
    legendOrder: [...config.legendOrder],
    sectorOverlayVisible: config.sectorOverlayVisible,
    sectorCount: config.sectorCount,
    speedDaysPerSecond: config.speedDaysPerSecond,
    scaleMode: config.scaleMode
  };
}

/**
 * Build the full shareable URL string for the current state (used by Story 5.2's
 * Share button and by the 5.1 e2e seam). Encodes the projected config into the URL
 * fragment on the current origin + path.
 */
export function buildShareUrl(state: AppState): string {
  const origin = typeof location !== 'undefined' ? location.origin : '';
  const pathname = typeof location !== 'undefined' ? location.pathname : '/';
  return `${origin}${pathname}#${encode(projectShareable(state))}`;
}

/**
 * Read the URL fragment (the ONLY reader). Returns the fragment WITHOUT the leading
 * `#`, or `''` when there is no fragment. Guarded for headless contexts.
 */
export function readShareFragment(): string {
  if (typeof location === 'undefined') return '';
  const hash = location.hash ?? '';
  return hash.startsWith('#') ? hash.slice(1) : hash;
}

/**
 * Write the current state's encoded config to the URL fragment (the ONLY writer).
 * Called by Story 5.2's Share button. Guarded for headless contexts.
 */
export function writeShareFragment(state: AppState): void {
  if (typeof location === 'undefined') return;
  location.hash = encode(projectShareable(state));
}
